import { SxProps, Theme } from '@mui/material';
import { WorkboardTask } from 'api/models/WorkboardTask';

export const groupedUserAutocompleteInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    minHeight: '32px',
    height: '32px',
    flexWrap: 'nowrap',
    py: '0 !important',
    fontSize: '0.8125rem',
  },
  '& .MuiAutocomplete-input': {
    py: '0 !important',
    fontSize: '0.8125rem',
    minWidth: '20px',
  },
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiInputLabel-shrink': { display: 'none' },
};

export const TASK_STATUS_OPTIONS = [
  { value: WorkboardTask.status.PENDING, label: 'Pending' },
  { value: WorkboardTask.status.IN_PROGRESS, label: 'In Progress' },
  { value: WorkboardTask.status.COMPLETED, label: 'Completed' },
  { value: WorkboardTask.status.CANCELLED, label: 'Cancelled' },
] as const;

export const TASK_STATUS_TRACKER_OPTIONS = TASK_STATUS_OPTIONS.map(option => ({
  id: option.value,
  label: option.label,
}));

export const RECURRENCE_FREQUENCY_OPTIONS = [
  { value: WorkboardTask.recurrence_frequency.NONE, label: 'Does not repeat' },
  { value: WorkboardTask.recurrence_frequency.DAILY, label: 'Daily' },
  { value: WorkboardTask.recurrence_frequency.WEEKLY, label: 'Weekly' },
  { value: WorkboardTask.recurrence_frequency.MONTHLY, label: 'Monthly' },
  { value: WorkboardTask.recurrence_frequency.YEARLY, label: 'Yearly' },
] as const;

export const RECURRENCE_WEEKDAY_OPTIONS = [
  { value: 'MO', label: 'Mon' },
  { value: 'TU', label: 'Tue' },
  { value: 'WE', label: 'Wed' },
  { value: 'TH', label: 'Thu' },
  { value: 'FR', label: 'Fri' },
] as const;

export const RECURRENCE_SETPOS_OPTIONS = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: 5, label: 'Fifth' },
  { value: -1, label: 'Last' },
] as const;

// Non-empty Select sentinels so the Monthly pattern label does not overlap the value.
export const RECURRENCE_MONTHLY_SAME_DAY_VALUE = 'same_day';
export const RECURRENCE_MONTHLY_FIRST_DAY_VALUE = 'first_day';
export const RECURRENCE_MONTHLY_LAST_DAY_VALUE = 'last_day';

export type RecurrenceEndMode = 'none' | 'date' | 'count';

export const RECURRENCE_FREQUENCY_HELPER_TEXT: Record<WorkboardTask.recurrence_frequency, string> = {
  [WorkboardTask.recurrence_frequency.NONE]: 'Creates a one-time task with no repeat schedule.',
  [WorkboardTask.recurrence_frequency.DAILY]:
    'Repeats every N business days starting from the due date. A weekend due date starts the following Monday.',
  [WorkboardTask.recurrence_frequency.WEEKLY]: 'Repeats every N weeks on the business days you select.',
  [WorkboardTask.recurrence_frequency.MONTHLY]:
    'Repeats monthly on the same calendar day, first/last day, or an ordinal weekday (e.g. first Monday). Weekend dates move to the next business day.',
  [WorkboardTask.recurrence_frequency.YEARLY]:
    'Repeats every N years on the same calendar date. Weekend dates move to the next business day.',
};

export const RECURRENCE_INTERVAL_HELPER_TEXT: Partial<Record<WorkboardTask.recurrence_frequency, string>> = {
  [WorkboardTask.recurrence_frequency.DAILY]: 'How often the task appears (in business days).',
  [WorkboardTask.recurrence_frequency.WEEKLY]: 'How often the weekly pattern repeats (in weeks).',
  [WorkboardTask.recurrence_frequency.MONTHLY]: 'How often the monthly pattern repeats (in months).',
  [WorkboardTask.recurrence_frequency.YEARLY]: 'How often the yearly pattern repeats (in years).',
};

export const RECURRENCE_SETPOS_SAME_DAY_HELPER_TEXT =
  'Uses the same day of the month as the due date (e.g. the 15th each month). If that day is a weekend, the task appears on the next business day.';

export const RECURRENCE_MONTHLY_FIRST_DAY_HELPER_TEXT =
  'Repeats on the first day of each month. If that day is a weekend, the task appears on the next business day.';

export const RECURRENCE_MONTHLY_LAST_DAY_HELPER_TEXT =
  'Repeats on the last day of each month. If that day is a weekend, the task appears on the next business day.';

export const RECURRENCE_SETPOS_ORDINAL_HELPER_TEXT =
  'Uses the Nth weekday of each month. Select weekdays below (e.g. First + Mon).';

export const RECURRENCE_WEEKDAY_WEEKLY_HELPER_TEXT = 'Choose which business days this task repeats on.';

export const RECURRENCE_WEEKDAY_MONTHLY_HELPER_TEXT = 'Required for ordinal monthly patterns (e.g. first Monday).';

export const RECURRENCE_ENDS_HELPER_TEXT: Record<RecurrenceEndMode, string> = {
  none: 'The series continues with no end date.',
  date: 'The series stops after this date (inclusive).',
  count: 'The series stops after this many occurrences, including the first.',
};

export const CREATE_TASK_NOTES_HELPER_TEXT =
  'Notes are available after you create the task. For recurring tasks, notes belong to each occurrence date.';
